/**
 * Hook for managing client consultations
 *
 * Provides CRUD operations for consultations, action items,
 * and frequency metrics.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/hooks/use-organization';

export interface Consultation {
  id: string;
  organization_id: string;
  client_id: string;
  trainer_id: string;
  scheduled_at: string | null;
  completed_at: string | null;
  duration_minutes: number;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  title: string | null;
  notes: string | null;
  action_items: ActionItemJson[];
  outcome_summary: string | null;
  next_consultation_date: string | null;
  consultation_type: 'initial' | 'monthly' | 'quarterly' | 'adhoc' | 'renewal';
  meeting_link: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  client?: {
    id: string;
    name: string;
    avatar_url: string | null;
  };
  trainer?: {
    id: string;
    name: string | null;
  };
}

export interface ActionItemJson {
  description: string;
  due_date?: string;
  assigned_to?: 'trainer' | 'client';
  is_completed?: boolean;
}

export interface ActionItem {
  id: string;
  consultation_id: string;
  client_id: string;
  description: string;
  due_date: string | null;
  assigned_to: 'trainer' | 'client' | null;
  is_completed: boolean;
  completed_at: string | null;
  created_at: string;
}

export interface ConsultationFrequency {
  total_consultations: number;
  completed_consultations: number;
  avg_days_between: number;
  last_consultation_at: string | null;
  next_consultation_at: string | null;
}

export interface CreateConsultationInput {
  client_id: string;
  scheduled_at?: string;
  duration_minutes?: number;
  title?: string;
  consultation_type?: Consultation['consultation_type'];
  meeting_link?: string;
  notes?: string;
}

export interface UpdateConsultationInput {
  id: string;
  scheduled_at?: string;
  completed_at?: string;
  duration_minutes?: number;
  status?: Consultation['status'];
  title?: string;
  notes?: string;
  outcome_summary?: string;
  next_consultation_date?: string;
  meeting_link?: string;
}

export interface CreateActionItemInput {
  consultation_id: string;
  client_id: string;
  description: string;
  due_date?: string;
  assigned_to?: 'trainer' | 'client';
}

// Fetch consultations for a client
export function useClientConsultations(clientId: string | undefined) {
  return useQuery({
    queryKey: ['consultations', 'client', clientId],
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from('consultations')
        .select(`
          *,
          trainer:profiles!consultations_trainer_id_fkey(id, name)
        `)
        .eq('client_id', clientId)
        .order('scheduled_at', { ascending: false });

      if (error) throw error;
      return data as Consultation[];
    },
    enabled: !!clientId,
  });
}

// Fetch upcoming consultations for trainer
export function useUpcomingConsultations() {
  const { organization } = useOrganization();

  return useQuery({
    queryKey: ['consultations', 'upcoming', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];

      const { data, error } = await supabase
        .from('consultations')
        .select(`
          *,
          client:clients!consultations_client_id_fkey(id, name, avatar_url)
        `)
        .eq('organization_id', organization.id)
        .eq('status', 'scheduled')
        .gte('scheduled_at', new Date().toISOString())
        .order('scheduled_at', { ascending: true })
        .limit(10);

      if (error) throw error;
      return data as Consultation[];
    },
    enabled: !!organization?.id,
  });
}

// Fetch consultation frequency metrics
export function useConsultationFrequency(clientId: string | undefined) {
  return useQuery({
    queryKey: ['consultation-frequency', clientId],
    queryFn: async () => {
      if (!clientId) return null;

      const { data, error } = await supabase
        .rpc('get_consultation_frequency', { p_client_id: clientId });

      if (error) throw error;
      return data?.[0] as ConsultationFrequency | null;
    },
    enabled: !!clientId,
  });
}

// Fetch pending action items for a client
export function usePendingActionItems(clientId: string | undefined) {
  return useQuery({
    queryKey: ['action-items', 'pending', clientId],
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .rpc('get_pending_action_items', { p_client_id: clientId });

      if (error) throw error;
      return data as Array<{
        id: string;
        consultation_id: string;
        description: string;
        due_date: string | null;
        assigned_to: string | null;
        is_overdue: boolean;
        consultation_date: string;
      }>;
    },
    enabled: !!clientId,
  });
}

// Consultation mutations
export function useConsultationMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { organization } = useOrganization();

  const createConsultation = useMutation({
    mutationFn: async (input: CreateConsultationInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      if (!organization?.id) throw new Error('No organization');

      const { data, error } = await supabase
        .from('consultations')
        .insert({
          ...input,
          organization_id: organization.id,
          trainer_id: user.id,
          action_items: [],
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      toast({
        title: 'Consultation scheduled',
        description: 'The consultation has been scheduled successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateConsultation = useMutation({
    mutationFn: async (input: UpdateConsultationInput) => {
      const { id, ...updates } = input;
      const { data, error } = await supabase
        .from('consultations')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      toast({
        title: 'Consultation updated',
        description: 'The consultation has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const completeConsultation = useMutation({
    mutationFn: async ({
      id,
      notes,
      outcome_summary,
      next_consultation_date
    }: {
      id: string;
      notes?: string;
      outcome_summary?: string;
      next_consultation_date?: string;
    }) => {
      const { data, error } = await supabase
        .from('consultations')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          notes,
          outcome_summary,
          next_consultation_date,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      queryClient.invalidateQueries({ queryKey: ['consultation-frequency'] });
      toast({
        title: 'Consultation completed',
        description: 'The consultation has been marked as complete.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const cancelConsultation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('consultations')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      toast({
        title: 'Consultation cancelled',
        description: 'The consultation has been cancelled.',
        duration: 6000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteConsultation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('consultations')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      queryClient.invalidateQueries({ queryKey: ['consultation-frequency'] });
      toast({
        title: 'Consultation deleted',
        description: 'The consultation has been deleted.',
        duration: 6000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    createConsultation,
    updateConsultation,
    completeConsultation,
    cancelConsultation,
    deleteConsultation,
  };
}

// Action item mutations
export function useActionItemMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createActionItem = useMutation({
    mutationFn: async (input: CreateActionItemInput) => {
      const { data, error } = await supabase
        .from('consultation_action_items')
        .insert(input)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
      toast({
        title: 'Action item created',
        description: 'The action item has been added.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const toggleActionItem = useMutation({
    mutationFn: async ({ id, is_completed }: { id: string; is_completed: boolean }) => {
      const { error } = await supabase
        .from('consultation_action_items')
        .update({
          is_completed,
          completed_at: is_completed ? new Date().toISOString() : null,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteActionItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('consultation_action_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
      toast({
        title: 'Action item deleted',
        duration: 6000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    createActionItem,
    toggleActionItem,
    deleteActionItem,
  };
}
